import React, { useEffect, useMemo, useState } from 'react';
import { reportsApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { BarChart3, Download, Loader2, Users, Eye } from 'lucide-react';

const iso = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const PRESETS = {
  today: () => { const t = new Date(); return [iso(t), iso(t)]; },
  yesterday: () => { const y = new Date(Date.now() - 864e5); return [iso(y), iso(y)]; },
  last7: () => [iso(new Date(Date.now() - 6 * 864e5)), iso(new Date())],
  mtd: () => { const t = iso(new Date()); return [`${t.slice(0, 8)}01`, t]; },
};
const PRESET_LABELS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['last7', 'Last 7 days'], ['mtd', 'Month to date'], ['custom', 'Custom']];

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-[#1f1f1f] bg-black/40 p-4" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-gray-300">{label}</div>
    <div className="mt-1 text-2xl font-bold text-white">{value}</div>
  </div>
);

const Table = ({ head, rows, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm min-w-[520px]">
      <thead><tr className="text-left text-[11px] uppercase text-gray-300 border-b border-[#1f1f1f]">{head.map(h => <th key={h} className="py-2 pr-3">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-[#1a1a1a]">{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className={`py-2 pr-3 ${c === 'Not recorded' ? 'text-gray-500 italic' : 'text-gray-100'}`}>{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

export default function AdminReportsPanel() {
  const [preset, setPreset] = useState('last7');
  const [range, setRange] = useState(PRESETS.last7());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const pick = (p) => { setPreset(p); if (p !== 'custom') setRange(PRESETS[p]()); };
  useEffect(() => {
    if (!range[0] || !range[1] || range[0] > range[1]) return;
    setBusy(true);
    reportsApi.summary(range[0], range[1]).then(setData)
      .catch(e => toast({ title: 'Could not load report', description: e?.response?.data?.detail || 'Try again' }))
      .finally(() => setBusy(false));
  }, [range]);

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await reportsApi.exportXlsx(range[0], range[1]);
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), { href: url, download: `PBB_Daily_Master_Report_${range[0]}_to_${range[1]}.xlsx` });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast({ title: 'Download failed', description: 'Please try again.' }); }
    setDownloading(false);
  };

  const ts = data?.trackingStarted || {};
  const since = useMemo(() => [['Logins', ts.members], ['Activity', ts.activity], ['Points snapshots', ts.points], ['Visitors', ts.visitors]], [ts]);

  return (
    <div className="space-y-5" data-testid="admin-reports">
      <div className="flex items-center gap-3 flex-wrap">
        <BarChart3 className="text-green-400" size={20}/>
        <div className="font-semibold text-white text-lg">Reports</div>
        <button onClick={download} disabled={downloading || !data} data-testid="reports-download"
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-sm font-semibold">
          {downloading ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>} Daily Master Report (Excel)
        </button>
      </div>

      <div className="pbb-card p-4 flex flex-wrap items-center gap-2" data-testid="reports-range">
        {PRESET_LABELS.map(([k, l]) => (
          <button key={k} onClick={() => pick(k)} data-testid={`reports-preset-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${preset === k ? 'bg-green-600 border-green-600 text-white' : 'border-[#2a2a2a] text-gray-200'}`}>{l}</button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2 text-xs text-gray-200">
            <input type="date" value={range[0]} max={range[1]} onChange={e => setRange([e.target.value, range[1]])} className="pbb-input !py-1.5" data-testid="reports-start"/>
            to
            <input type="date" value={range[1]} min={range[0]} onChange={e => setRange([range[0], e.target.value])} className="pbb-input !py-1.5" data-testid="reports-end"/>
          </div>
        )}
        <span className="ml-auto text-xs text-gray-300">{range[0]} → {range[1]} (Eastern time)</span>
      </div>

      <div className="text-xs text-gray-300" data-testid="reports-tracking-since">
        Tracking since: {since.map(([l, d]) => <span key={l} className="mr-3">{l}: <b className="text-gray-100">{d || 'Not recorded yet'}</b></span>)}
        <span className="block mt-1 text-gray-400">Signup dates go back to the start of the app. Everything else counts only from the day tracking began; earlier days show "Not recorded".</span>
      </div>

      {busy || !data ? <div className="flex items-center gap-2 text-gray-300"><Loader2 size={14} className="animate-spin"/> Loading…</div> : (
        <>
          <div className="pbb-card p-5 space-y-4">
            <div className="flex items-center gap-2 font-semibold text-white"><Users size={16} className="text-green-400"/> Members</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Total members" value={data.members.total} testId="rep-members-total"/>
              <Stat label="New signups in range" value={data.members.signupsInRange} testId="rep-signups"/>
              <Stat label="Allow texts" value={data.members.textsAllowed} testId="rep-texts"/>
              <Stat label="Allow emails" value={data.members.emailsAllowed} testId="rep-emails"/>
              {data.communityReports && <Stat label="Community reports (open)" value={`${data.communityReports.total} (${data.communityReports.open})`} testId="rep-community-reports"/>}
              {data.newsletter && <Stat label="Newsletter emails sent / failed" value={`${data.newsletter.totals.send_accepted} / ${data.newsletter.totals.send_failed}`} testId="rep-nl-sent"/>}
              {data.newsletter && <Stat label="Newsletter subscribes / unsubscribes" value={`${data.newsletter.totals.subscribed} / ${data.newsletter.totals.unsubscribed}`} testId="rep-nl-subs"/>}
              {data.newsletter && <Stat label="Newsletter button clicks" value={data.newsletter.totals.click} testId="rep-nl-clicks"/>}
              {Object.entries(data.members.groups).map(([k, n]) => <Stat key={k} label={k} value={n} testId={`rep-group-${k.split(' ')[0].toLowerCase()}`}/>)}
            </div>
            <p className="text-[11px] text-gray-400">Active = logged in within 7 days · Cooling = 8–30 days · Inactive = more than 30 days. Full member list is in the Excel file.</p>
          </div>

          <div className="pbb-card p-5 space-y-4">
            <div className="flex items-center gap-2 font-semibold text-white"><Eye size={16} className="text-green-400"/> Visitors (not signed in)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Unique visitors" value={data.visitors.uniqueVisitors} testId="rep-unique-visitors"/>
              <Stat label="Total visits" value={data.visitors.visits} testId="rep-visits"/>
              <Stat label="Page views" value={data.visitors.pageViews} testId="rep-pageviews"/>
              <Stat label="Then signed up" value={data.visitors.signups} testId="rep-visitor-signups"/>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <Table testId="rep-sources" head={['Source', 'Visits', 'Unique visitors']} rows={data.visitors.sources.map(s => [s.source, s.visits, s.visitors])}/>
              <Table testId="rep-pages" head={['Page', 'Visits']} rows={data.visitors.topPages.length ? data.visitors.topPages.slice(0, 10).map(p => [p.path, p.visits]) : [['No page views yet', '']]}/>
            </div>
          </div>

          <div className="pbb-card p-5">
            <div className="font-semibold text-white mb-3">Daily totals</div>
            <Table testId="rep-daily" head={['Day', 'New signups', 'Active members', 'Unique visitors', 'Visits', 'Page views', 'Visitor signups']}
              rows={data.daily.map(d => [d.day, d.signups, d.activeMembers, d.uniqueVisitors, d.visits, d.pageViews, d.visitorSignups])}/>
          </div>
        </>
      )}
    </div>
  );
}
